import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import {
  faAddressBook,
  faBolt,
  faBook,
  faChartLine,
  faChevronDown,
  faCog,
  faMessages,
  faSignInAlt,
  faStethoscope,
  faTriangleExclamation,
  faUser,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { CSSProvider } from "@perfectserve/css-styles-react";

import { PERMISSIONS } from "../constants/permissions";
import { useTransition } from "../contexts/TransitionContext";
import { useAppAuth, useUser } from "../hooks";

import "./components.css";

// Navigation item interface
interface NavigationItem {
  route: string;
  name: string;
  shortName?: string; // For medium screens
  color: string;
  icon: IconDefinition;
  permission?: {
    requirePerfectServeUser?: boolean;
    requirePermission?: string;
    customCheck?: (isPerfectServeUser: boolean, hasPermission: (perm: string) => boolean) => boolean;
  };
}

// Navigation items configuration
const navigationItems: NavigationItem[] = [
  {
    route: "/interactions",
    name: "Interactions",
    color: "#27ae60",
    icon: faChartLine,
    permission: {
      customCheck: (isPerfectServeUser, hasPermission) =>
        isPerfectServeUser || hasPermission(PERMISSIONS.CSP_AFTER_HOURS_REPORTING),
    },
  },
];

// Entire navigation row
const _fullNavigationItems: NavigationItem[] = [
  {
    route: "/messaging",
    name: "Messaging",
    color: "#214f7c",
    icon: faMessages,
    // No permission required - all authenticated users can access
  },
  {
    route: "/directory",
    name: "Directory",
    color: "#3f2b56",
    icon: faAddressBook,
    // No permission required - all authenticated users can access
  },
  {
    route: "/scheduling",
    name: "Scheduling",
    color: "#ff9e1a",
    icon: faBolt,
    permission: {
      requirePerfectServeUser: true,
    },
  },
  {
    route: "/patients",
    name: "Patients",
    color: "#3f2b56",
    icon: faStethoscope,
    permission: {
      requirePerfectServeUser: true,
    },
  },
  {
    route: "/interactions",
    name: "Interactions",
    color: "#27ae60",
    icon: faChartLine,
    permission: {
      customCheck: (isPerfectServeUser, hasPermission) =>
        isPerfectServeUser || hasPermission(PERMISSIONS.CSP_AFTER_HOURS_REPORTING),
    },
  },
  {
    route: "/caremobility",
    name: "Care Mobility",
    color: "#00abc7",
    icon: faTriangleExclamation,
    permission: {
      customCheck: (isPerfectServeUser, hasPermission) =>
        isPerfectServeUser || hasPermission(PERMISSIONS.HOSPITAL_PRACTITIONER_ADMINISTRATOR),
    },
  },
  {
    route: "/documentation",
    name: "Documentation",
    color: "#7b7a7f",
    icon: faBook,
    permission: {
      customCheck: (isPerfectServeUser) => isPerfectServeUser,
    },
  },
];

interface NavigationProps {
  isVisible?: boolean;
}

const Navigation = ({ isVisible = true }: NavigationProps) => {
  const location = useLocation();
  const { navigate } = useTransition();
  const { isAuthenticated, login, logout, isPerfectServeUser, hasPermission, isPermissionsLoading } = useAppAuth();
  const { user, selectedRole, openRoleSelector } = useUser();

  // User dropdown state
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
  const userDropdownRef = useRef<HTMLDivElement>(null);

  // Responsive state
  const [isMobile, setIsMobile] = useState(false);
  const [visibleItems, setVisibleItems] = useState<NavigationItem[]>([]);

  // Refs for measuring
  const navContainerRef = useRef<HTMLDivElement>(null);
  const navButtonsRef = useRef<HTMLDivElement>(null);

  // Check screen size for responsive behavior
  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth;
      setIsMobile(width < 768); // Tablet and smaller screens show only icons
    };

    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);

    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  // Helper function to check if user can access a navigation item
  const canAccessItem = (item: NavigationItem): boolean => {
    if (!item.permission) return true; // No permission required

    const { requirePerfectServeUser, requirePermission, customCheck } = item.permission;

    if (requirePerfectServeUser && !isPerfectServeUser) return false;
    if (requirePermission && !hasPermission(requirePermission)) return false;
    if (customCheck) {
      return customCheck(isPerfectServeUser, hasPermission);
    }

    return true;
  };

  // Function to calculate which items should be visible based on permissions
  const calculateVisibleItems = React.useCallback(() => {
    if (!isAuthenticated) {
      setVisibleItems([]);
      return;
    }

    // Get all accessible navigation items
    const accessibleItems = navigationItems.filter(canAccessItem);
    setVisibleItems(accessibleItems);
  }, [navigationItems, isAuthenticated, isMobile, isPerfectServeUser, hasPermission, isPermissionsLoading]);

  // Calculate initial state and when dependencies change
  useEffect(() => {
    calculateVisibleItems();
  }, [calculateVisibleItems]);

  // Handle window resize with debouncing
  useEffect(() => {
    let resizeTimeout: NodeJS.Timeout;

    const handleResize = () => {
      // Clear existing timeout
      clearTimeout(resizeTimeout);

      // Update mobile state immediately for responsive layout
      const screenWidth = window.innerWidth;
      const newIsMobile = screenWidth < 768;
      if (newIsMobile !== isMobile) {
        setIsMobile(newIsMobile);
      }

      // Debounce the recalculation to trigger after resize stops
      resizeTimeout = setTimeout(() => {
        // Trigger immediate recalculation
        calculateVisibleItems();
      }, 300); // Wait 300ms after resize stops
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      clearTimeout(resizeTimeout);
    };
  }, [isMobile]);

  // Helper function to get navigation button classes
  const getNavButtonClasses = (path: string) => {
    const isActive = location.pathname === path;
    return `navigation-button ${isActive ? "active" : ""}`;
  };

  // Helper function to render a navigation button
  const renderNavButton = (item: NavigationItem, showTextLabel: boolean = true) => {
    return (
      <button
        type="button"
        key={item.route}
        onClick={() => navigate(item.route)}
        className={getNavButtonClasses(item.route)}
        style={
          {
            "--nav-color": item.color,
            "--nav-color-light": `${item.color}1A`,
          } as React.CSSProperties
        }
        title={item.name} // Always show full name in tooltip
      >
        <div className="navigation-button-indicator" />
        <FontAwesomeIcon icon={item.icon} className="navigation-button-icon" />
        {showTextLabel && !isMobile && (item.shortName || item.name)}
      </button>
    );
  };

  // Set default selected role when user data is loaded
  // Note: Role selection is now handled by AuthContext
  // No automatic role selection here to avoid conflicts with the role selector modal

  // Handle clicking outside user dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userDropdownRef.current && !userDropdownRef.current.contains(event.target as Node)) {
        setIsUserDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <CSSProvider>
      <nav
        ref={navContainerRef}
        className={`navigation-container ${isVisible ? "navigation-visible" : ""}`}
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
          backgroundColor: "#ffffff",
          minHeight: "50px",
          maxHeight: "50px",
        }}
      >
        <div className="navigation-content">
          <div className="d-flex align-items-center">
            <div
              onClick={isPerfectServeUser ? () => navigate("/admin") : undefined}
              onKeyDown={
                isPerfectServeUser
                  ? (e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        navigate("/admin");
                      }
                    }
                  : undefined
              }
              className={`navigation-logo ${isPerfectServeUser ? "clickable" : ""} ${isPermissionsLoading ? "loading" : ""}`}
              tabIndex={isPerfectServeUser ? 0 : -1}
              role={isPerfectServeUser ? "button" : undefined}
              title={isPerfectServeUser ? "Admin" : isPermissionsLoading ? "Loading permissions..." : undefined}
            />

            {isAuthenticated && (
              <div ref={navButtonsRef} className="navigation-buttons-container">
                {visibleItems.map((item) => renderNavButton(item))}
              </div>
            )}
          </div>

          <div className="user-section">
            {isAuthenticated ? (
              <div ref={userDropdownRef} className="user-dropdown-container">
                {/* User Dropdown Button */}
                <button
                  type="button"
                  onClick={() => setIsUserDropdownOpen(!isUserDropdownOpen)}
                  className="user-dropdown-button"
                >
                  <FontAwesomeIcon icon={faUser} />
                  {!isMobile && <span>{user?.displayName || user?.firstName || "User"}</span>}
                  <FontAwesomeIcon
                    icon={faChevronDown}
                    className={`user-dropdown-chevron ${isUserDropdownOpen ? "open" : ""}`}
                  />
                </button>

                {/* Dropdown Menu */}
                {isUserDropdownOpen && (
                  <div className="user-dropdown-menu">
                    {/* Current Persona Display - Only show if user has multiple roles */}
                    {user && user.roles && user.roles.length > 1 && (
                      <div className="user-dropdown-current-persona">
                        <div className="user-dropdown-current-persona-label">Current Persona:</div>
                        <div className="user-dropdown-current-persona-value">
                          {!selectedRole && "No role selected"}
                          {(selectedRole && selectedRole.isPractitioner && selectedRole.displayName) || "Practitioner"}
                          {selectedRole &&
                            selectedRole.facilityName &&
                            `${selectedRole.facilityName} - ${selectedRole.roleName}`}
                        </div>
                      </div>
                    )}

                    {/* Change Persona Link - Only show if user has multiple roles */}
                    {user && user.roles && user.roles.length > 1 && (
                      <button
                        type="button"
                        className="user-dropdown-menu-item"
                        onClick={() => {
                          setIsUserDropdownOpen(false);
                          openRoleSelector();
                        }}
                      >
                        <FontAwesomeIcon icon={faUser} />
                        Change Persona
                      </button>
                    )}

                    {/* Settings Option */}
                    <button
                      type="button"
                      className="user-dropdown-menu-item"
                      onClick={() => {
                        setIsUserDropdownOpen(false);
                        navigate("/human");
                      }}
                    >
                      <FontAwesomeIcon icon={faCog} />
                      Settings
                    </button>

                    {/* Sign Out Option */}
                    <button
                      type="button"
                      className="user-dropdown-menu-item sign-out"
                      onClick={() => {
                        setIsUserDropdownOpen(false);
                        logout();
                      }}
                    >
                      Sign Out
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <button type="button" onClick={login} className="login-button">
                <FontAwesomeIcon icon={faSignInAlt} className="mr-8" />
                Login
              </button>
            )}
          </div>
        </div>
      </nav>
    </CSSProvider>
  );
};

export default Navigation;
